import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Award, CreditCard, Bell, Calendar, TrendingUp, Brain, ChevronRight, BookOpen, Phone, GraduationCap, Users } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../api';

const QUICK = [
  { label: 'Results',       icon: Award,      from: 'from-emerald-500', to: 'to-emerald-400', bg: 'bg-emerald-50', text: 'text-emerald-700', path: '/parent/results' },
  { label: 'Fees',          icon: CreditCard, from: 'from-amber-500',   to: 'to-amber-400',   bg: 'bg-amber-50',   text: 'text-amber-700',   path: '/parent/fees' },
  { label: 'Announcements', icon: Bell,       from: 'from-rose-500',    to: 'to-rose-400',    bg: 'bg-rose-50',    text: 'text-rose-600',    path: '/parent/announcements' },
  { label: 'Events',        icon: Calendar,   from: 'from-sky-500',     to: 'to-sky-400',     bg: 'bg-sky-50',     text: 'text-sky-700',     path: '/parent/events' },
];

export default function ParentDashboard() {
  const { user, school } = useAuth();
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);

  const firstName = user?.first_name || 'Parent';
  const hour      = new Date().getHours();
  const greeting  = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const emoji     = hour < 12 ? '🌅' : hour < 17 ? '✌️' : '🌙';
  const today     = new Date().toLocaleDateString('en-NG', { weekday: 'long', day: 'numeric', month: 'long' });

  useEffect(() => {
    api.get('/exams/parent/children').then(r => { if (r?.success) setChildren(r.data || []); }).catch(() => {});
  }, []);

  return (
    <div className="p-4 lg:p-6 max-w-5xl mx-auto space-y-5" data-testid="parent-dashboard-page">

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-slate-900 leading-tight">
            {greeting}, {firstName}! {emoji}
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">{today} · {school?.name || 'Parent Portal'}</p>
        </div>
        <div className="bg-blue-100 border border-blue-200 text-blue-700 px-3 py-1.5 rounded-2xl text-sm font-bold flex-shrink-0">
          Parent
        </div>
      </div>

      {/* Hero Banner */}
      <div
        className="relative overflow-hidden rounded-3xl p-6 shadow-lg"
        style={{ background: 'linear-gradient(135deg, #2563eb 0%, #1d58d4 50%, #1e40af 100%)' }}
        data-testid="parent-hero-banner"
      >
        <div className="absolute inset-0 pointer-events-none select-none" aria-hidden>
          <div className="absolute -top-10 -right-10 w-56 h-56 rounded-full bg-white/5" />
          <div className="absolute bottom-0 left-1/3 w-44 h-44 rounded-full bg-white/5" />
        </div>
        <div className="relative z-10">
          <p className="text-blue-200 text-xs font-bold uppercase tracking-widest mb-2">Parent Dashboard</p>
          <h2 className="text-white font-black text-2xl mb-1">Stay Close to What Matters</h2>
          <p className="text-white/80 text-sm mb-5 max-w-md">
            Track your children's academic progress, fee payments, and school communications.
          </p>
          <div className="flex gap-3 flex-wrap">
            <button
              onClick={() => navigate('/parent/results')}
              className="bg-white text-[#2563eb] font-bold px-5 py-2.5 rounded-full text-sm hover:bg-white/90 active:scale-95 transition-transform shadow-sm"
              data-testid="hero-view-results-btn"
            >
              View Results
            </button>
            <button
              onClick={() => navigate('/parent/ai')}
              className="bg-white/15 border border-white/20 text-white font-semibold px-5 py-2.5 rounded-full text-sm hover:bg-white/25 backdrop-blur-sm transition-colors flex items-center gap-2"
              data-testid="hero-ai-guide-btn"
            >
              <Brain className="w-4 h-4" /> AI Guide
            </button>
          </div>
        </div>
      </div>

      {/* Children Cards */}
      {children.length > 0 && (
        <div>
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
            <Users className="w-3.5 h-3.5" /> My Children
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {children.map(c => (
              <div
                key={c.id}
                className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm hover:shadow-md transition-shadow"
                data-testid={`child-card-${c.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-[#2563eb]/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#2563eb] font-black text-sm">
                      {c.first_name?.charAt(0)}{c.last_name?.charAt(0)}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-slate-800 text-sm truncate">{c.first_name} {c.last_name}</p>
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                      {c.class_name && (
                        <span className="flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full font-medium">
                          <GraduationCap className="w-3 h-3" /> {c.class_name}
                        </span>
                      )}
                    </div>
                    {c.school_name && (
                      <p className="text-[10px] text-slate-400 mt-1 truncate">{c.school_name}</p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => navigate('/parent/results')}
                  className="w-full mt-3 text-xs text-[#2563eb] font-semibold bg-blue-50 hover:bg-blue-100 py-1.5 rounded-xl transition-colors"
                >
                  View Results
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Access */}
      <div>
        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Quick Access</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {QUICK.map((q) => (
            <button
              key={q.label}
              onClick={() => navigate(q.path)}
              className={`${q.bg} p-4 rounded-2xl text-left hover:-translate-y-0.5 hover:shadow-md active:scale-[0.98] transition-all duration-200`}
              data-testid={`parent-quick-${q.label.toLowerCase()}`}
            >
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${q.from} ${q.to} flex items-center justify-center mb-3 shadow-sm`}>
                <q.icon className="w-5 h-5 text-white" />
              </div>
              <p className={`text-sm font-bold ${q.text}`}>{q.label}</p>
              <ChevronRight className="w-3.5 h-3.5 text-slate-400 mt-0.5" />
            </button>
          ))}
        </div>
      </div>

      {/* Info Row */}
      <div className="grid lg:grid-cols-2 gap-4">
        {/* School Info */}
        <div className="bg-white rounded-3xl border border-slate-100 p-5 shadow-sm">
          <h3 className="font-bold text-slate-800 text-sm mb-4">School Contact</h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 rounded-2xl bg-slate-50">
              <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-4 h-4 text-[#2563eb]" />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-800">{school?.name || 'School Name'}</p>
                <p className="text-xs text-slate-500">{school?.address || school?.city || 'Nigeria'}</p>
              </div>
            </div>
            {school?.phone && (
              <div className="flex items-center gap-3 p-3 rounded-2xl bg-slate-50">
                <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-4 h-4 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Phone</p>
                  <p className="text-sm font-semibold text-slate-800">{school.phone}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* AI Card */}
        <div
          className="rounded-3xl p-5 relative overflow-hidden"
          style={{ background: 'linear-gradient(160deg, #2563eb 0%, #1e40af 100%)' }}
          data-testid="parent-ai-card"
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/4" />
          <div className="relative z-10">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center mb-4">
              <Brain className="w-5 h-5 text-white" />
            </div>
            <h3 className="font-bold text-white mb-1">EDUVA AI Parent Guide</h3>
            <p className="text-blue-100 text-sm mb-4">
              Get tips to support your child's studies and understand school policies.
            </p>
            <button
              onClick={() => navigate('/parent/ai')}
              className="bg-white/15 border border-white/20 text-white text-sm font-semibold px-4 py-2 rounded-xl hover:bg-white/25 transition-colors"
              data-testid="open-ai-guide-btn"
            >
              Chat with AI
            </button>
          </div>
        </div>
      </div>

    </div>
  );
}
